import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import type { MaterialCatalog, SupplierPrice, PriceHistory } from '../types';

export interface Supplier { id: string; name: string }

// Prix comparé pour un matériel
export interface ComparedPrice {
  material: MaterialCatalog;
  prices: (SupplierPrice & { supplier_name: string })[];
  bestPrice?: number;
  bestSupplierId?: string;
  maxPrice?: number;
  savings?: number;
}

// Panier
export interface BasketItem {
  material: MaterialCatalog;
  quantity: number;
}

export type ViewMode = 'search' | 'catalog' | 'basket';

export interface BLReference {
  bl_number: string;
  bl_date: string;
  supplier_name: string;
  project_name: string;
  quantity: number;
  unit_price: number;
}

export interface PriceFormState {
  supplier_id: string;
  catalog_price: string;
  net_price: string;
  stock_status: string;
  delivery_days: string;
}

export interface NewMaterialState {
  reference: string;
  designation: string;
  brand: string;
  category: string;
  unit: string;
}

export function usePriceComparatorData() {
  const [loading, setLoading] = useState(true);

  // Data
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [catalog, setCatalog] = useState<MaterialCatalog[]>([]);

  // Vue
  const [viewMode, setViewMode] = useState<ViewMode>('search');

  // Recherche & comparaison
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ComparedPrice[]>([]);
  const [searching, setSearching] = useState(false);

  // Catalogue management
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState<NewMaterialState>({ reference: '', designation: '', brand: '', category: '', unit: 'pce' });
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [catalogSearch, setCatalogSearch] = useState('');

  // Prix management
  const [editingPriceFor, setEditingPriceFor] = useState<string | null>(null); // material_id
  const [priceForm, setPriceForm] = useState<PriceFormState>({ supplier_id: '', catalog_price: '', net_price: '', stock_status: 'unknown', delivery_days: '' });

  // Historique des prix
  const [historyMaterialId, setHistoryMaterialId] = useState<string | null>(null);
  const [priceHistoryData, setPriceHistoryData] = useState<PriceHistory[]>([]);

  // Panier
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [basketPrices, setBasketPrices] = useState<ComparedPrice[]>([]);

  // Import
  const [importing, setImporting] = useState(false);

  // Cross-référence BL
  const [blMaterialId, setBlMaterialId] = useState<string | null>(null);
  const [blReferences, setBlReferences] = useState<BLReference[]>([]);

  // ─── FETCH INITIAL ───────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: supplierData }, { data: catalogData }] = await Promise.all([
      supabase.from('suppliers').select('id, name').order('name'),
      supabase.from('material_catalog').select('*').eq('is_active', true).order('reference')
    ]);
    if (supplierData) setSuppliers(supplierData);
    if (catalogData) setCatalog(catalogData);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Listes uniques pour filtres
  const brands = useMemo(() => [...new Set(catalog.map(m => m.brand).filter(Boolean))] as string[], [catalog]);
  const categories = useMemo(() => [...new Set(catalog.map(m => m.category).filter(Boolean))] as string[], [catalog]);

  // Catalogue filtré
  const filteredCatalog = useMemo(() => {
    return catalog.filter(m => {
      if (filterBrand !== 'all' && m.brand !== filterBrand) return false;
      if (filterCategory !== 'all' && m.category !== filterCategory) return false;
      if (catalogSearch) {
        const s = catalogSearch.toLowerCase();
        if (!m.reference.toLowerCase().includes(s) && !m.designation.toLowerCase().includes(s) && !(m.brand || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [catalog, filterBrand, filterCategory, catalogSearch]);

  // Chart data pour l'historique
  const historyChartData = useMemo(() => {
    if (!priceHistoryData.length) return null;
    const supplierIds = [...new Set(priceHistoryData.map(h => h.supplier_id))];
    const colors = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    return {
      labels: [...new Set(priceHistoryData.map(h => new Date(h.recorded_at).toLocaleDateString('fr-FR')))],
      datasets: supplierIds.map((suppId, i) => {
        const points = priceHistoryData.filter(h => h.supplier_id === suppId);
        return {
          label: points[0]?.supplier?.name || 'Fournisseur',
          data: points.map(p => p.net_price || p.catalog_price || 0),
          borderColor: colors[i % colors.length],
          backgroundColor: colors[i % colors.length] + '20',
          tension: 0.3,
          pointRadius: 4
        };
      })
    };
  }, [priceHistoryData]);

  // ─── INDICATEURS D'INFLATION ─────────────────────────────────────────
  const inflationIndicators = useMemo(() => {
    if (!priceHistoryData.length) return [];
    const supplierIds = [...new Set(priceHistoryData.map(h => h.supplier_id))];
    return supplierIds.map(suppId => {
      const points = priceHistoryData
        .filter(h => h.supplier_id === suppId)
        .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());
      if (points.length < 2) return null;
      const firstPrice = points[0].net_price || points[0].catalog_price || 0;
      const lastPrice = points[points.length - 1].net_price || points[points.length - 1].catalog_price || 0;
      if (firstPrice === 0) return null;
      const delta = ((lastPrice - firstPrice) / firstPrice) * 100;
      return {
        supplierName: points[0]?.supplier?.name || 'Fournisseur',
        delta: Math.round(delta * 10) / 10,
        firstPrice,
        lastPrice,
        nbPoints: points.length
      };
    }).filter(Boolean) as { supplierName: string; delta: number; firstPrice: number; lastPrice: number; nbPoints: number }[];
  }, [priceHistoryData]);

  // ─── PANIER : compute on change ──────────────────────────────────────
  const computeBasket = useCallback(async () => {
    if (basket.length === 0) return;
    const materialIds = basket.map(b => b.material.id);
    const { data: prices } = await supabase
      .from('supplier_prices')
      .select('*, supplier:suppliers(id, name)')
      .in('material_id', materialIds)
      .eq('is_active', true);

    const compared: ComparedPrice[] = basket.map(item => {
      const materialPrices = (prices || [])
        .filter(p => p.material_id === item.material.id)
        .map(p => ({ ...p, supplier_name: p.supplier?.name || 'Inconnu' }));

      const netPrices = materialPrices.map(p => p.net_price || p.catalog_price || Infinity).filter(p => p !== Infinity);
      const bestPrice = netPrices.length > 0 ? Math.min(...netPrices) : undefined;
      const maxPrice = netPrices.length > 1 ? Math.max(...netPrices) : undefined;
      const bestSupplierId = materialPrices.find(p => (p.net_price || p.catalog_price) === bestPrice)?.supplier_id;
      const savings = bestPrice && maxPrice ? maxPrice - bestPrice : undefined;

      return { material: item.material, prices: materialPrices, bestPrice, bestSupplierId, maxPrice, savings };
    });

    setBasketPrices(compared);
  }, [basket]);

  useEffect(() => { if (basket.length > 0) computeBasket(); }, [basket, computeBasket]);

  // Total panier optimal
  const basketTotal = useMemo(() => {
    return basket.reduce((total, item) => {
      const compared = basketPrices.find(bp => bp.material.id === item.material.id);
      const price = compared?.bestPrice || 0;
      return total + price * item.quantity;
    }, 0);
  }, [basket, basketPrices]);

  // Total panier prix max (pour calculer économies)
  const basketMaxTotal = useMemo(() => {
    return basket.reduce((total, item) => {
      const compared = basketPrices.find(bp => bp.material.id === item.material.id);
      const price = compared?.maxPrice || compared?.bestPrice || 0;
      return total + price * item.quantity;
    }, 0);
  }, [basket, basketPrices]);

  return {
    // loading
    loading,
    // data
    suppliers, setSuppliers,
    catalog, setCatalog,
    // view
    viewMode, setViewMode,
    // search
    searchQuery, setSearchQuery,
    searchResults, setSearchResults,
    searching, setSearching,
    // catalog management
    showAddForm, setShowAddForm,
    newMaterial, setNewMaterial,
    filterBrand, setFilterBrand,
    filterCategory, setFilterCategory,
    catalogSearch, setCatalogSearch,
    // price form
    editingPriceFor, setEditingPriceFor,
    priceForm, setPriceForm,
    // history
    historyMaterialId, setHistoryMaterialId,
    priceHistoryData, setPriceHistoryData,
    // basket
    basket, setBasket,
    basketPrices, setBasketPrices,
    // import
    importing, setImporting,
    // BL refs
    blMaterialId, setBlMaterialId,
    blReferences, setBlReferences,
    // refresh
    fetchData,
    // computed
    brands,
    categories,
    filteredCatalog,
    historyChartData,
    inflationIndicators,
    basketTotal,
    basketMaxTotal,
  };
}
